import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Container } from './styles';

import ColaboradorService from '../../services/ColaboradorService';
import AuthService from '../../services/AuthService';
import history from '../../services/history';
import {
  getPageParams,
  errorHandle,
  padLeft,
  calcNaxItemsPage,
  isScreenMobile,
} from '../../util/functions';
import LabelSp from '../../components/LabelSp';

import { showMessage } from '../../components/MessageDialog';
import InputTextSp from '../../components/InputTextSp';

import BotaoMenuGrid from '../../components/BotaoMenuGrid';

import ColaboradorCrud from './crud';
import { StateScreen } from '../constants';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';
import DropDownLazy from '../../components/DropdownLazy';
import ColaboradorPerfilService from '../../services/ColaboradorPerfilService';
import ColaboradorVinculoService from '../../services/ColaboradorVinculoService';
import DropdownSp from '../../components/DropdownSp';

export default function Colaborador(props) {
  // useSelectors
  const filterVisible = useSelector(state => state.global.filterVisible);

  // useMemo
  const pageParams = useMemo(() => getPageParams(props.match.params, props.location.search), [
    props.location.search,
    props.match.params,
  ]);

  const filterService = useMemo(() => ColaboradorService.getFilter(), []);

  // useStates
  const toBack = pageParams.toBack || '/colaboradores';

  const [filter, setFilter] = useState(filterService);
  const [colaboradores, setColaboradores] = useState([]);
  const [colaboradorVinculo, setColaboradorVinculo] = useState(null);
  const [colaboradorPerfil, setColaboradorPerfil] = useState(null);

  const [pageLimit, setPageLimit] = useState(filterService.limit);
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);
  const [funcTimeOut, setFuncTimeOut] = useState();

  // useCallbacks
  const loadColaboradorPerfil = useCallback(async _nome => {
    if (_nome !== undefined) {
      const r = await ColaboradorPerfilService.findAll({ nome: _nome, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const loadColaboradorVinculo = useCallback(async _nome => {
    if (_nome !== undefined) {
      const r = await ColaboradorVinculoService.findAll({ nome: _nome, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const calcLimit = useCallback(() => {
    let ret;
    if (filterVisible) {
      ret = calcNaxItemsPage(10, 14, 19);
    } else {
      ret = calcNaxItemsPage(13, 17, 22);
    }
    setPageLimit(ret);
    return ret;
  }, [filterVisible]);

  const handleBuscar = useCallback(
    async (_filter, _page, resetPage = true) => {
      _filter.page = _page || 0;
      _filter.limit = calcLimit();
      try {
        const result = await ColaboradorService.findAll(_filter);
        setColaboradores(result.items);

        setTotalRecords(result.totalRecords);
        if (resetPage) {
          setFirst(0);
        }
      } catch (err) {
        errorHandle(err);
      }
    },
    [calcLimit]
  );

  const setFilterAndSearch = useCallback(
    async _filterValue => {
      if (JSON.stringify(_filterValue) !== JSON.stringify(filter)) {
        setFilter(_filterValue);

        if (funcTimeOut) {
          clearTimeout(funcTimeOut);
        }
        const func = setTimeout(async () => {
          handleBuscar(_filterValue);
        }, 800);
        setFuncTimeOut(func);
      }
    },
    [filter, funcTimeOut, handleBuscar]
  );

  function onPage(event) {
    const pagina = event.first / event.rows;
    setPageSelected(pagina);
    setFirst(event.first);
    handleBuscar(filter, pagina, false);
  }

  // useCallbacks
  const excluirRegistro = useCallback(
    async _id => {
      try {
        await ColaboradorService.delete(_id);
        toast.success('Registro excluído com sucesso.');
        handleBuscar(filter);
      } catch (err) {
        errorHandle(err);
      }
    },
    [filter, handleBuscar]
  );

  // functions
  function getTitle() {
    const titleDefault = 'Colaborador';
    let titleAdd = '';

    if (pageParams.stateScreen === StateScreen.stSearch) {
      titleAdd = '';
    }
    if (pageParams.stateScreen === StateScreen.stInsert) {
      titleAdd = '(Novo)';
    }
    if (pageParams.stateScreen === StateScreen.stUpdate) {
      titleAdd = `(Alterando Id: ${pageParams.idSelected})`;
    }
    if (pageParams.stateScreen === StateScreen.stView) {
      titleAdd = ` (Visualizando Id: ${pageParams.idSelected})`;
    }

    if (!isScreenMobile()) {
      return `${titleDefault} ${titleAdd}`;
    }

    return titleDefault;
  }

  const confirmaExclusao = useCallback(
    id => {
      showMessage('Confirmação', 'Confirma a exclusão do registro?', idx => {
        if (idx === 1) {
          excluirRegistro(id);
        }
      });
    },
    [excluirRegistro]
  );

  // useEffects
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      handleBuscar(filter, 0, true);
    }
    // desativado para evitar que a cada vez que o colaborador digitasse o sistema buscasse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffects
  // Responsavel por recalcular a quantidade de registros da grid quando a div do filtro ocultar
  // ou ficar visivel
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      calcLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcLimit]);

  // renders
  return (
    <Container className="container-page">
      <div className="p-grid">
        <CrudHeader
          title={getTitle()}
          disabledButtonInsert={!AuthService.checkRoles('COLABORADOR_INSERT')}
          showButtonFilter={pageParams.stateScreen === StateScreen.stSearch}
          showButtonInsert
          showButtonSearch
          titleFilterDefault="Buscar por Nome"
          handleFilterDefault={text => {
            filter.nome = text;
            setFilter({ ...filter, nome: text });
            handleBuscar(filter);
          }}
          handleClearFilters={() => {
            setFilter({
              ...filter,
              nome: '',
              email: '',
              idColaboradorPerfil: undefined,
              idColaboradorVinculo: undefined,
              inativo: '',
            });
            setColaboradorVinculo(null);
            setColaboradorPerfil(null);
          }}
          handleButtonInsert={() => history.push('/colaboradores/insert')}
          handleButtonSearch={() => handleBuscar(filter)}
        >
          <div className="p-col-12 p-sm-4 p-lg-4 p-fluid">
            <LabelSp>E-mail</LabelSp>
            <InputTextSp
              value={filter.email}
              maxLength={100}
              onChange={e => {
                setFilterAndSearch({ ...filter, email: e.target.value });
              }}
            />
          </div>
          <div className="p-col-12 p-sm-3 p-lg-3 p-fluid">
            <LabelSp>Perfil</LabelSp>
            <DropDownLazy
              autoLoad
              showClear
              placeholder="Todos"
              onChange={e => {
                setFilterAndSearch({ ...filter, idColaboradorPerfil: e?.value });
                setColaboradorPerfil(e);
              }}
              value={colaboradorPerfil}
              onFilter={async txtFilter => {
                const retorno = await loadColaboradorPerfil(txtFilter);
                return retorno;
              }}
            />
          </div>
          <div className="p-col-6 p-sm-3 p-lg-3 p-fluid">
            <LabelSp>Vínculo</LabelSp>
            <DropDownLazy
              autoLoad
              showClear
              placeholder="Todos"
              onChange={e => {
                setFilterAndSearch({ ...filter, idColaboradorVinculo: e?.value });
                setColaboradorVinculo(e);
              }}
              value={colaboradorVinculo}
              onFilter={async txtFilter => {
                const retorno = await loadColaboradorVinculo(txtFilter);
                return retorno;
              }}
            />
          </div>
          <div className="p-col-6 p-sm-2 p-lg-2 p-fluid">
            <LabelSp>Inativo?</LabelSp>
            <DropdownSp
              value={filter.inativo}
              options={[
                { label: 'Todos', value: '' },
                { label: 'Sim', value: 'true' },
                { label: 'Não', value: 'false' },
              ]}
              filterInputAutoFocus={false}
              onChange={e => {
                setFilterAndSearch({ ...filter, inativo: e?.value });
              }}
            />
          </div>
        </CrudHeader>
        {pageParams.stateScreen === StateScreen.stSearch ? renderSearch() : renderCrud()}
      </div>
    </Container>
  );

  function renderSearch() {
    return (
      <>
        <div className="p-col-12 p-fluid datatable-doc-demo">
          <DataTableSp
            value={colaboradores}
            style={{ marginBottom: '2px' }}
            paginator
            rows={pageLimit}
            lazy
            responsive
            totalRecords={totalRecords}
            first={first}
            onPage={onPage}
          >
            <Column
              field="id"
              body={rowData => padLeft(rowData.id, 6)}
              header="Id"
              className="grid-col-id"
            />
            <Column field="nome" className="grid-col" header="Nome" />
            <Column field="email" className="grid-col" header="E-mail" />
            <Column
              className="grid-col p-p-5"
              style={{ width: 200 }}
              header="Perfil"
              body={rowData => rowData.colaboradorPerfilNome}
            />
            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </>
    );
  }

  function renderButtonOp(rowData) {
    return (
      <BotaoMenuGrid
        handles={[
          () => history.push(`/colaboradores/${rowData.id}?view`),
          () => history.push(`/colaboradores/${rowData.id}`),
          () => confirmaExclusao(rowData.id),
        ]}
        disableds={[
          false,
          !AuthService.checkRoles('COLABORADOR_UPDATE'),
          !AuthService.checkRoles('COLABORADOR_DELETE'),
        ]}
      />
    );
  }

  function renderCrud() {
    return (
      <ColaboradorCrud
        idSelected={pageParams.idSelected}
        stateScreen={pageParams.stateScreen}
        onClose={() => {
          history.push(toBack);
          handleBuscar(filter, pageSelected, false);
        }}
      />
    );
  }
}
